import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Favorite } from '../models/WeatherData';
import { StorageService } from '../services/StorageService';
import { useLanguage } from '../contexts/LanguageContext';

interface MapAndControlsProps {
  coordinates: { lat: number; lon: number };
  selectedDate: string;
  locationName: string;
  onDateChange: (date: string) => void;
  onLocationChange: (coords: { lat: number; lon: number }) => void;
  onCurrentLocation: () => void;
  onShowToast?: (message: string) => void;
}

const MapAndControls: React.FC<MapAndControlsProps> = ({
  coordinates,
  selectedDate,
  locationName,
  onDateChange,
  onLocationChange,
  onCurrentLocation,
  onShowToast,
}) => {
  const { t } = useLanguage();
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [favorites, setFavorites] = useState<Favorite[]>([]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Initialize map
    const map = L.map(mapContainerRef.current, {
      zoomControl: false
    }).setView([coordinates.lat, coordinates.lon], 10);

    // Add OpenStreetMap tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(map);

    // Custom marker icon - clean SVG pin
    const svgIcon = `
      <svg width="32" height="40" viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="16" cy="37" rx="8" ry="3" fill="rgba(0,0,0,0.2)"/>
        <path d="M16 0C9.373 0 4 5.373 4 12c0 8.5 12 26 12 26s12-17.5 12-26c0-6.627-5.373-12-12-12z" 
              fill="#3B82F6" stroke="#1E40AF" stroke-width="1.5"/>
        <circle cx="16" cy="12" r="5" fill="white"/>
        <circle cx="16" cy="12" r="2.5" fill="#1E40AF"/>
      </svg>
    `;
    
    const customIcon = L.icon({
      iconUrl: 'data:image/svg+xml;base64,' + btoa(svgIcon),
      iconSize: [32, 40],
      iconAnchor: [16, 40],
      popupAnchor: [0, -40]
    });

    const marker = L.marker([coordinates.lat, coordinates.lon], { icon: customIcon }).addTo(map);
    markerRef.current = marker;

    // Click handler
    map.on('click', (e) => {
      onLocationChange({ lat: e.latlng.lat, lon: e.latlng.lng });
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapRef.current && markerRef.current) {
      mapRef.current.setView([coordinates.lat, coordinates.lon], mapRef.current.getZoom());
      markerRef.current.setLatLng([coordinates.lat, coordinates.lon]);
    }
  }, [coordinates]);

  useEffect(() => {
    const loadedFavorites = StorageService.getFavorites();
    setFavorites(loadedFavorites);
  }, [coordinates]);

  const handleAddFavorite = () => {
    StorageService.addFavorite(coordinates.lat, coordinates.lon, locationName);
    setFavorites(StorageService.getFavorites());
    if (onShowToast) {
      onShowToast(t.locationAdded);
    }
  };

  const handleRemoveFavorite = (id: number) => {
    StorageService.removeFavorite(id);
    setFavorites(StorageService.getFavorites());
    if (onShowToast) {
      onShowToast(t.locationRemoved);
    }
  };

  const handleSelectFavorite = (fav: Favorite) => {
    onLocationChange({ lat: fav.lat, lon: fav.lng });
  };

  const handleShare = async () => {
    const url = `${window.location.origin}?lat=${coordinates.lat}&lon=${coordinates.lon}&date=${selectedDate}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: t.weatherAppTitle,
          text: `${t.checkWeatherFor} ${locationName}`,
          url: url
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      await navigator.clipboard.writeText(url);
      alert(t.linkCopied);
    }
  };

  return (
    <div className="rounded-2xl sm:rounded-3xl bg-white/[0.02] p-3 sm:p-4 backdrop-blur-xl border border-white/[0.05] shadow-lg sticky top-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm sm:text-base font-semibold text-white/90">🌍 {t.selectLocation}</h2>
        <span className="text-[10px] sm:text-xs text-white/60 hidden sm:block">{t.clickToExplore}</span>
      </div>
      
      <div className="relative mb-4">
        <div ref={mapContainerRef} id="map" className="h-[140px] sm:h-[160px] lg:h-[180px] xl:h-[200px] w-full rounded-xl overflow-hidden" />
        
        {/* Position knapp i kartan */}
        <button 
          id="current-location-btn"
          onClick={onCurrentLocation}
          className="absolute top-3 right-3 flex items-center justify-center rounded-xl bg-white/95 hover:bg-white shadow-lg hover:shadow-xl text-gray-700 hover:text-blue-600 p-3 transition-all duration-200 backdrop-blur-sm border border-white/50 group z-[10003]" 
          title={t.currentLocationBtn}
        >
          <span className="text-lg group-hover:scale-110 transition-transform">📍</span>
        </button>
      </div>
      
      {/* Datum kontroll under kartan */}
      <div>
        <label className="block text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
          {t.selectDate}
        </label>
        <input 
          id="date-input"
          type="date" 
          value={selectedDate}
          onChange={(e) => onDateChange(e.target.value)}
          min={(() => {
            const date = new Date();
            date.setDate(date.getDate() - 365 * 10);
            return date.toISOString().split('T')[0];
          })()}
          max={(() => {
            const date = new Date();
            date.setDate(date.getDate() + 10);
            return date.toISOString().split('T')[0];
          })()}
          className="w-full rounded-xl bg-white/[0.03] px-3 py-2.5 text-sm font-medium text-white placeholder-slate-400 border border-white/[0.08] focus:border-blue-400 focus:bg-white/[0.05] focus:outline-none focus:ring-0 transition-all min-h-[40px] appearance-none" 
        />
        
        {/* Dela-knapp */}
        <button 
          id="share-btn"
          onClick={handleShare}
          className="w-full mt-3 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 hover:border-blue-400/50 text-blue-300 hover:text-blue-200 px-3 py-2.5 text-xs font-medium transition-all duration-200 flex items-center justify-center gap-2 group"
        >
          <span className="text-sm group-hover:scale-110 transition-transform">🔗</span>
          <span>{t.shareWeather}</span>
        </button>
      </div>
      
      {/* Favorit platser i samma sidebar */}
      <div id="favorites-container-wrapper" className="mt-4 pt-4 border-t border-white/[0.08]">
        <div className="mb-3">
          <label className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-slate-400">
            ⭐ {t.favorites}
          </label>
        </div>
        
        <div id="favorites-container" className="favorites-list space-y-2 max-h-32 overflow-y-auto overscroll-contain scroll-smooth">
          {favorites.length === 0 ? (
            <div id="no-favorites" className="text-[10px] text-slate-500 text-center py-3">
              {t.noFavorites}
            </div>
          ) : (
            favorites.map((fav) => (
              <div
                key={fav.id}
                className="bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] rounded-lg p-2 sm:p-2.5 flex items-center justify-between group smooth-transition"
              >
                <button
                  onClick={() => handleSelectFavorite(fav)}
                  className="flex-1 text-left"
                >
                  <p className="text-xs font-medium text-white truncate">
                    {fav.name}
                  </p>
                  <p className="text-[10px] text-slate-400">
                    {fav.lat.toFixed(4)}, {fav.lng.toFixed(4)}
                  </p>
                </button>
                <button
                  onClick={() => handleRemoveFavorite(fav.id)}
                  className="ml-2 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 smooth-transition text-xs"
                  title={t.removeFromFavorites}
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </div>
        
        <button
          onClick={handleAddFavorite}
          className="w-full mt-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] text-slate-300 hover:text-white px-2 py-1.5 text-xs font-medium smooth-transition"
        >
          + {t.addToFavorites}
        </button>
      </div>
    </div>
  );
};

export default MapAndControls;
